/**
 * =============================================================================
 * MEDIA STATUS BADGE COMPONENT
 * =============================================================================
 *
 * Color-coded badge for media moderation status (ADR-022 §10(C)).
 * Mirrors ReplyStatusBadge for visual consistency across moderation surfaces.
 *
 * BL-IMG-ADMIN-MODERATION-001
 *
 * =============================================================================
 */

import React from 'react';
import { StatusChip } from '../common/StatusChip';
import type { MediaModerationStatus } from '@find-my-part/types';

export interface MediaStatusBadgeProps {
  status: MediaModerationStatus;
  'data-testid'?: string;
}

interface StatusConfig {
  label: string;
  variant: 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
}

/**
 * - PENDING_REVIEW: warning (yellow) — needs attention
 * - APPROVED: success (green) — publicly servable
 * - REJECTED: danger (red) — terminal
 * - HIDDEN: secondary (gray) — origin-denied
 */
const MEDIA_STATUS_CONFIG: Record<MediaModerationStatus, StatusConfig> = {
  PENDING_REVIEW: { label: 'Pending', variant: 'warning' },
  APPROVED: { label: 'Approved', variant: 'success' },
  REJECTED: { label: 'Rejected', variant: 'danger' },
  HIDDEN: { label: 'Hidden', variant: 'secondary' },
};

export function MediaStatusBadge({
  status,
  'data-testid': testId,
}: MediaStatusBadgeProps): React.ReactElement {
  const config = MEDIA_STATUS_CONFIG[status];

  return (
    <StatusChip
      variant={config.variant}
      label={config.label}
      testId={testId}
      ariaLabel={`Media status: ${config.label}`}
    />
  );
}
